import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SportsTennisRoundedIcon from "@mui/icons-material/SportsTennisRounded";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

function getDurationUnit(cadence) {
  switch (cadence) {
    case "DAILY":
      return "Days";
    case "WEEKLY":
      return "Weeks";
    case "MONTHLY":
      return "Months";
    case "ANNUAL":
      return "Years";
    default:
      return "";
  }
}

function extractPlanDetails(variation) {
  const phase = variation?.subscription_plan_variation_data?.phases?.[0] || {};
  const price = Number(phase?.pricing?.price?.amount || 0);
  const duration = Number(phase?.periods) || 0;
  const cadence = phase?.cadence || "";

  const cleanName = (variation?.subscription_plan_variation_data?.name || "")
    .replace("Variation", "")
    .trim();

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "AUD",
  }).format(price / 100);

  const durationLabel =
    duration > 0 ? `${duration} ${getDurationUnit(cadence)}` : "Forever";

  return {
    variationId: variation.id,
    name: cleanName || "N/A",
    shortName: {
      "Unlimited Pennants": cleanName.includes("Multi"),
      "Single Pennant": cleanName.includes("Single"),
      "Gym Only 24/7": cleanName.includes("Gym"),
    },
    price: formattedPrice,
    priceNumber: price,
    paymentFrequency: cadence,
    durationNumber: duration,
    duration: durationLabel,
    features: {
      "24/7 Access": cleanName.includes("- 24/7"),
      "Unlimited Pennants Per Season": cleanName.includes("Multi"),
      "One Pennant Per Season": cleanName.includes("Single"),
      "Gym Access": cleanName.includes("Gym"),
    },
    category: cleanName.includes("Junior") ? "Junior" : "Adult",
  };
}

function sortVariationsByCadence(variations) {
  const orderMap = { WEEKLY: 1, MONTHLY: 2, ANNUAL: 3 };
  return variations.slice().sort((a, b) => {
    const orderA = orderMap[a.paymentFrequency] ?? 99;
    const orderB = orderMap[b.paymentFrequency] ?? 99;
    return orderA - orderB;
  });
}

export default function MemberAndPennantInfo() {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  const [membershipType, setMembershipType] = useState("Annual");
  const [category, setCategory] = useState("Adult");

  const [pennants, setPennants] = useState([]);
  const [loadingPennants, setLoadingPennants] = useState(false);
  const [selectedDay, setSelectedDay] = useState("Monday");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchPennants = async () => {
      setLoadingPennants(true);
      try {
        const fetchPennantsPublic = httpsCallable(functions, "fetchPennantsPublic");
        const result = await fetchPennantsPublic();
        const { pennants: fetchedPennants } = result.data || {};
        setPennants(fetchedPennants || []);
      } catch (error) {
        console.error("Error fetching pennants:", error);
      } finally {
        setLoadingPennants(false);
      }
    };
    fetchPennants();
  }, [functions]);

  useEffect(() => {
    const fetchPlansFunc = httpsCallable(functions, "fetchSquarePlans");
    const fetchPlans = async () => {
      setLoadingPlans(true);
      try {
        const { data } = await fetchPlansFunc();
        const flattened = data.plans
          .filter((plan) => !plan.is_deleted && plan.present_at_all_locations)
          .flatMap((plan) =>
            plan.subscription_plan_data?.subscription_plan_variations
              ?.filter((variation) => !variation.is_deleted)
              .map((variation) => extractPlanDetails(variation))
          );
        setPlans(flattened);
      } catch (error) {
        console.error("Error fetching plans:", error?.message || error || "Unknown error");
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [functions]);

  // eslint-disable-next-line arrow-body-style
  const filterPlans = (type) => {
    return plans.filter((plan) =>
      type === "Other"
        ? !plan.name.includes("Annual") && !plan.name.includes("Seasonal")
        : plan.name.includes(type)
    );
  };

  const membershipLabels = {
    Annual: "Per Year",
    Seasonal: "Per Season",
    Other: "Other",
  };

  const groupPlansByName = (plansArray) => {
    const grouped = {};
    plansArray.forEach((plan) => {
      if (!grouped[plan.name]) {
        grouped[plan.name] = [];
      }
      grouped[plan.name].push(plan);
    });
    return grouped;
  };

  const groupPennantsByDay = () => {
    const days = {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    };
    pennants.forEach((pennant) => {
      const day = pennant.pennantDay || "Unknown";
      if (days[day]) {
        days[day].push(pennant);
      }
    });
    return days;
  };

  const dayAbbreviations = {
    Monday: "M",
    Tuesday: "T",
    Wednesday: "W",
    Thursday: "T",
    Friday: "F",
    Saturday: "S",
    Sunday: "S",
  };

  const renderPlans = (groupedPlans) => {
    const groupedArray = Object.entries(groupedPlans);

    groupedArray.sort(([_, variationsA], [__, variationsB]) => {
      const aIsUnlimited = variationsA.some(
        (v) => v.shortName["Unlimited Pennants"]
      );
      const bIsUnlimited = variationsB.some(
        (v) => v.shortName["Unlimited Pennants"]
      );
      if (aIsUnlimited && !bIsUnlimited) return -1;
      if (!aIsUnlimited && bIsUnlimited) return 1;
      return 0;
    });

    return (
      <Grid container spacing={3}>
        {groupedArray.map(([name, planVariations]) => {
          const shortName = Object.keys(planVariations[0].shortName).find(
            (key) => planVariations[0].shortName[key]
          );
          const firstVariation = planVariations[0];

          const sortedVariations = sortVariationsByCadence(planVariations);

          return (
            <Grid item xs={12} md={6} lg={4} key={name}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {shortName || name}
                  </Typography>
                  <Divider sx={{ my: 1 }} />

                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1 }}
                  >
                    Includes:
                  </Typography>
                  <List dense disablePadding>
                    {Object.entries(firstVariation.features).map(
                      ([feature, included]) =>
                        included && (
                          <ListItem
                            key={feature}
                            sx={{ py: 0.5, alignItems: "flex-start" }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <SportsTennisRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primaryTypographyProps={{ variant: "body2" }}
                              primary={feature}
                            />
                          </ListItem>
                        )
                    )}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  {sortedVariations.map((variation, index) => {
                    const displayFrequency =
                      variation.paymentFrequency === "ANNUAL"
                        ? "UPFRONT"
                        : variation.paymentFrequency;

                    return (
                      <Box
                        key={variation.variationId || index}
                        sx={{ mb: index < sortedVariations.length - 1 ? 2 : 0 }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {displayFrequency} Price:{" "}
                          <Box
                            component="span"
                            sx={{
                              color: "primary.main",
                              fontWeight: "bold",
                            }}
                          >
                            {variation.price}
                          </Box>
                        </Typography>

                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Total Cost:{" "}
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "AUD",
                            }).format(
                              (variation.priceNumber *
                                variation.durationNumber) /
                                100
                            )}
                          </Box>
                        </Typography>

                        {index < sortedVariations.length - 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderPennantsByDay = (day) => {
    const pennantsByDay = groupPennantsByDay()[day] || [];
    if (pennantsByDay.length > 0) {
      return (
        <List>
          {pennantsByDay.map((pennant) => (
            <ListItem key={pennant.id}>
              <ListItemText
                primary={`
                  ${pennant.time || ""}
                  ${pennant.sport ? `${pennant.sport} ` : ""}
                  ${pennant.name || `Pennant ${pennant.id}`}
                `.trim()}
              />
            </ListItem>
          ))}
        </List>
      );
    }
    return (
      <Typography variant="body2" color="textSecondary">
        No pennants available for {day}.
      </Typography>
    );
  };

  const days = Object.keys(groupPennantsByDay());

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}
      >
        Current Membership Plans
      </Typography>

      <Paper
        elevation={1}
        sx={{
          mb: 4,
          p: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#f7f7f7" : "#2c2c2c",
        }}
      >
        <ToggleButtonGroup
          value={membershipType}
          exclusive
          onChange={(e, newValue) => {
            if (newValue) setMembershipType(newValue);
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="Annual">Per Year</ToggleButton>
          <ToggleButton value="Seasonal">Per Season</ToggleButton>
          <ToggleButton value="Other">Other</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={(e, newValue) => {
            if (newValue) setCategory(newValue);
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="Adult">Adult</ToggleButton>
          <ToggleButton value="Junior">Junior</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ minHeight: 300 }}>
          {loadingPlans ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "inherit",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            (() => {
              const filtered = filterPlans(membershipType).filter(
                (plan) => plan.category === category
              );
              if (filtered.length === 0) {
                return (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 2 }}
                  >
                    No membership plans available for this selection.
                  </Typography>
                );
              }
              return renderPlans(groupPlansByName(filtered));
            })()
          )}
        </Box>
      </Paper>

      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
      >
        Current Pennants
      </Typography>

      <Paper
        elevation={1}
        sx={{
          p: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#f7f7f7" : "#2c2c2c",
        }}
      >
        <ToggleButtonGroup
          value={selectedDay}
          exclusive
          onChange={(e, newValue) => {
            if (newValue) setSelectedDay(newValue);
          }}
          sx={{
            mb: 2,
            flexWrap: "wrap",
            [theme.breakpoints.down("sm")]: {
              transform: "scale(0.9)",
              transformOrigin: "top left",
            },
          }}
        >
          {days.map((day) => {
            const dayAbbrev = isMobile ? dayAbbreviations[day] : day;
            return (
              <ToggleButton key={day} value={day} sx={{ mb: 1 }}>
                {dayAbbrev}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        <Box sx={{ minHeight: 200 }}>
          {loadingPennants ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "inherit",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            renderPennantsByDay(selectedDay)
          )}
        </Box>
      </Paper>
    </Box>
  );
}
